/**
 * Marketplace basket rollup backfill service — SuperAdmin-only operator
 * tool for seeding / re-aggregating per-(basket, date) rollup rows.
 *
 * Backed by `POST /admin/marketplace/backfill-rollups`. The endpoint is
 * shared by the preview (`dry_run: true`) and live (`dry_run: false`)
 * paths so the dry-run is a faithful simulation of the live run.
 */
import apiClient from './apiClient';

const API_TYPE = 'api' as const;

export interface BackfillRequest {
  start_date: string;   // YYYY-MM-DD (IST calendar)
  end_date: string;     // YYYY-MM-DD (IST calendar), must be >= start_date
  basket_ids?: string[]; // optional — defaults to ALL published baskets
  dry_run?: boolean;     // defaults to false on the wire
}

export interface BackfillFailedCell {
  basket_id: string;
  target_date: string;
  error: string;
}

export interface BackfillResponse {
  success: boolean;
  run_id: string;
  dry_run: boolean;
  actor_user_id: string;
  start_date: string;
  end_date: string;
  baskets_processed?: number;
  dates_in_window?: number;
  cells_processed?: number;
  /** Present when dry_run=true — count of cells the live run WOULD write. */
  cells_simulated?: number;
  /** Present when the synchronous live response returns — count of rollup
   *  rows actually written. Not present when the live run was kicked off
   *  asynchronously (see `status`). */
  rows_written?: number;
  errors?: number;
  started_at: string;
  finished_at?: string;
  failed_cells?: BackfillFailedCell[];
  /** "RUNNING" when the live run was kicked off async (API GW would otherwise
   *  hit its 30s integration timeout). Operator checks the BACKFILL_RUN#
   *  audit row in analytics-daily for completion. */
  status?: 'RUNNING' | 'COMPLETE';
  message?: string;
}

/** Shape of the audit-row GET response. Superset of `BackfillResponse` —
 *  includes runtime progress (`total_cells`, `baskets_processed`) the
 *  kickoff stamps so the polling UI can render an estimated denominator. */
export interface BackfillStatusResponse {
  success: boolean;
  run_id: string;
  status: 'RUNNING' | 'COMPLETE';
  dry_run: boolean;
  actor_user_id: string;
  started_at: string;
  finished_at: string;
  start_date: string;
  end_date: string;
  basket_ids_filter?: string[];
  total_cells?: number;
  baskets_processed?: number;
  rows_written?: number;
  errors?: number;
  failed_items?: BackfillFailedCell[];
}

class MarketplaceBackfillService {
  /**
   * Preview the backfill — does not write any rollup rows. The audit row
   * IS written (with `dry_run=true`) so a compliance trail captures every
   * preview the operator ran before the live invocation.
   */
  async preview(request: Omit<BackfillRequest, 'dry_run'>): Promise<BackfillResponse> {
    return apiClient.post<BackfillResponse>(
      '/admin/marketplace/backfill-rollups',
      { ...request, dry_run: true },
      undefined,
      API_TYPE,
    );
  }

  /**
   * Execute the backfill — writes one rollup row per (basket, date) cell.
   * Idempotent — re-running the same range overwrites cleanly. Returns
   * status=RUNNING with a `run_id` the caller polls via `getStatus`
   * (async kickoff is required because API Gateway caps the integration
   * timeout at 30s).
   */
  async run(request: Omit<BackfillRequest, 'dry_run'>): Promise<BackfillResponse> {
    return apiClient.post<BackfillResponse>(
      '/admin/marketplace/backfill-rollups',
      { ...request, dry_run: false },
      undefined,
      API_TYPE,
    );
  }

  /**
   * Poll the status of an in-flight backfill. The page polls every ~3s
   * after kicking off a live run; stops when `status === 'COMPLETE'`.
   */
  async getStatus(runId: string): Promise<BackfillStatusResponse> {
    return apiClient.get<BackfillStatusResponse>(
      `/admin/marketplace/backfill-rollups/${encodeURIComponent(runId)}`,
      undefined,
      API_TYPE,
    );
  }
}

const marketplaceBackfillService = new MarketplaceBackfillService();
export default marketplaceBackfillService;
